import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';
import { print } from 'graphql';
import { EventEmitter } from 'events';
import * as Sentry from '@sentry/react';
import { getFirebaseTokenFromSW } from 'serviceWorker';
import { FirebaseAuth } from 'services/firebase';
import {
  attachClientEventSender,
  detachClientEventSender,
  reportExpected,
} from 'services/observability';

// ──────────────────────────────────────────────────────────────────────────────
// "graphql-transport-ws" subprotocol message types.
// Protocol spec: https://github.com/enisdenjo/graphql-ws/blob/master/PROTOCOL.md
//
// The backend (django-channels-graphql-ws kodo fork v1.0.0rc7-kodo1) added
// support for this modern subprotocol in rc7 (2023-08-17). The subprotocol is
// negotiated during the WebSocket handshake via Sec-WebSocket-Protocol.
//
// Palco-specific custom messages (heartbeat, track_presence, update_auth_token)
// are NOT part of the protocol but are intercepted by the Palco Django consumer
// before the library sees them, so they do not trigger the 4400 "wrong type" close.
// ──────────────────────────────────────────────────────────────────────────────
const SUBPROTOCOL = 'graphql-transport-ws';

/**
 * Upper bound on how long the handshake waits for an auth token.
 *
 * The socket has to open even if neither the service worker nor Firebase ever
 * answers: with REACT_APP_CONNECTION_ON_WEBSOCKET=ALL this link carries
 * queries and mutations too, so a handshake that waits forever takes the whole
 * app down rather than merely leaving the user anonymous.
 */
const AUTH_TOKEN_TIMEOUT_MS = 3_000;

/**
 * Which source won the handshake's token race. `timeout` means neither
 * answered in time and the connection proceeds anonymously.
 */
type AuthTokenSource = 'serviceWorker' | 'firebase' | 'timeout';

type AuthTokenResult = { token: string | null; source: AuthTokenSource };

/**
 * Reported at most once per page load: the timeout is rare by construction, and
 * one event per session is enough to spot it without risking the Sentry quota.
 */
let authTimeoutReported = false;

/**
 * A real timeout measures about `AUTH_TOKEN_TIMEOUT_MS`. A far larger figure
 * means the timer itself was suspended — a backgrounded tab, or a sleeping
 * device — so the wait never happened in any sense the user experienced, and
 * it says nothing about whether the token sources are healthy.
 *
 * Measured in production on 10/10/2026: 219874 ms, on a tab that had slept for
 * 83 minutes. The breadcrumb still records it, flagged; only the event is
 * suppressed.
 */
const SUSPENDED_TAB_FACTOR = 2;

/**
 * Observability for the handshake's auth race. The failure mode this guards
 * against does not throw — the token simply never arrives and the connection
 * goes out anonymous — so without an explicit probe it is invisible.
 *
 * The breadcrumb is free (it is only delivered attached to some other event)
 * and gives any later error the handshake's history. The captureMessage fires
 * only when the timeout cost a signed-in user their identity: `lateResult` is
 * the token race the timeout cut short, and the event waits for it. A visitor
 * with no account loses nothing by going out anonymous, and on slow in-app
 * browsers they hit the timeout all the time (818 users in October 2026, almost
 * all Facebook on Android), so for them the breadcrumb is enough.
 *
 * Wrapped whole: an observability failure must never break the connection.
 */
const reportHandshakeAuth = (
  source: AuthTokenSource,
  elapsedMs: number,
  authenticated: boolean,
  lateResult?: Promise<AuthTokenResult>,
): void => {
  const suspended = elapsedMs > AUTH_TOKEN_TIMEOUT_MS * SUSPENDED_TAB_FACTOR;
  // The whole point is readable at a glance in the breadcrumb list, where
  // `data` stays collapsed: whether the connection carries an identity, where
  // it came from, how long it took. Worded to avoid the words Sentry's data
  // scrubber reacts to — the previous message said "auth token" and arrived
  // as `[Filtered]`, which hid exactly the line someone triaging looks for.
  const detail = suspended
    ? `timeout, tab suspended ${elapsedMs} ms`
    : source === 'timeout'
    ? `no answer within ${elapsedMs} ms`
    : `${source}, ${elapsedMs} ms`;
  const message = `WS handshake: ${
    authenticated ? 'signed in' : 'anonymous'
  } (${detail})`;
  try {
    Sentry.addBreadcrumb({
      category: 'ws.handshake',
      level: 'info',
      message,
      data: { source, elapsedMs, authenticated, suspended },
    });
    if (source === 'timeout' && !suspended) reportExpected('ws_auth_timeout');
    if (source === 'timeout' && !suspended && lateResult) {
      const startedAt = Date.now() - elapsedMs;
      lateResult
        .then(({ token }) => {
          if (!token || authTimeoutReported) return;
          authTimeoutReported = true;
          Sentry.captureMessage('WS handshake: auth token timed out', {
            level: 'warning',
            extra: {
              elapsedMs,
              timeoutMs: AUTH_TOKEN_TIMEOUT_MS,
              // When the token finally arrived: how much longer it would have taken.
              tokenAfterMs: Date.now() - startedAt,
            },
          });
        })
        .catch(() => undefined);
    }
  } catch {
    // Never let the probe affect the handshake.
  }
};

/** Resolves with `fallback` when `promise` has not settled within `ms`. */
const settleWithin = <T,>(
  promise: Promise<T>,
  ms: number,
  fallback: T,
): Promise<T> =>
  new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    const settle = (value: T) => {
      clearTimeout(timer);
      resolve(value);
    };
    promise.then(settle, () => settle(fallback));
  });

const GQL = {
  // Client → Server
  CONNECTION_INIT: 'connection_init',
  SUBSCRIBE: 'subscribe',
  CLIENT_COMPLETE: 'complete', // client sends to unsubscribe
  PONG: 'pong', // client responds to server PING

  // Server → Client
  CONNECTION_ACK: 'connection_ack',
  NEXT: 'next', // subscription data payload
  ERROR: 'error', // subscription-level error
  SERVER_COMPLETE: 'complete', // server signals subscription ended
  PING: 'ping', // server sends; client must respond with PONG
} as const;

type ActiveSub = {
  observer: {
    next: (v: FetchResult) => void;
    error: (e: unknown) => void;
    complete: () => void;
  };
  operation: Operation;
};

type WsOptions = {
  reconnect?: boolean;
  reconnectionAttempts?: number;
  timeout?: number;
};

/** Shared event bus so external code can send raw WS messages. */
export const WsConnector = new EventEmitter();

/**
 * Connection-status events broadcast on `WsConnector` so React code can surface
 * a global connectivity indicator without coupling to the WS internals.
 *
 *  - `connected`    : CONNECTION_ACK received — the link is healthy.
 *  - `reconnecting` : a reconnect attempt was scheduled (`attempt` = nth try).
 *  - `lost`         : max reconnect attempts exhausted — the link gave up.
 */
export type WsConnectionStatus =
  | { state: 'connected' }
  | { state: 'reconnecting'; attempt: number }
  | { state: 'lost' };

export const WS_CONNECTION_STATUS_EVENT = 'connection_status';

/**
 * Apollo Link that implements the modern "graphql-transport-ws" WebSocket subprotocol.
 * Replaces `subscriptions-transport-ws` without requiring backend changes (the
 * django-channels-graphql-ws rc7 fork already supports both subprotocols).
 *
 * Preserved behaviours:
 *  - Firebase auth token injected in `connection_init` and refreshed via
 *    `update_auth_token` on every auth-state / id-token change.
 *  - Heartbeat every 30 s (keeps presence alive + server-side HEARTBEAT handler).
 *  - Custom Palco messages: `track_presence`, `end_track_presence` via WsConnector.
 *  - Exponential-backoff reconnection (default 10 attempts, cap 30 s).
 *  - Pending-queue: subscriptions started before CONNECTION_ACK are buffered and
 *    flushed on ack; active subscriptions are re-queued on disconnect.
 */
export class WsSubscriptionClient extends ApolloLink {
  private url: string;
  private ws: WebSocket | null = null;
  private connected = false;
  private connecting = false;
  /**
   * True once the auth token has been delivered on the current socket. Distinct
   * from `connected`, which flips at `connection_ack`: between the two an
   * operation would travel on a connection the server still sees as anonymous,
   * so new operations keep queueing until this is set.
   */
  private authReady = false;

  private subscriptions = new Map<string, ActiveSub>();
  private pendingQueue: Array<{ opId: string; sub: ActiveSub }> = [];

  private opIdCounter = 0;
  private heartbeatInterval: NodeJS.Timeout | undefined;
  private reconnectTimer: NodeJS.Timeout | undefined;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts: number;

  private readonly firebaseAppAuth: FirebaseAuth;
  private last_sent_token: string | undefined;
  private readonly connectionParamsFactory: () => Promise<
    Record<string, unknown>
  >;

  constructor(firebaseAppAuth: FirebaseAuth, url: string, options?: WsOptions) {
    super();
    this.url = url;
    this.firebaseAppAuth = firebaseAppAuth;
    this.maxReconnectAttempts = options?.reconnectionAttempts ?? 10;

    // ── Build connection_init auth params ──────────────────────────────────
    const getTokenFromFirebase = () =>
      new Promise<AuthTokenResult>((resolve) => {
        const done = (token: string | null) =>
          resolve({ token, source: 'firebase' });
        // One-shot listener: resolve immediately after first auth state is known.
        const unsubscribe = firebaseAppAuth.onAuthStateChanged((user) => {
          unsubscribe();
          if (user) {
            user.getIdToken().then(done);
          } else {
            done(null);
          }
        });
      });

    // The service worker answers `null` immediately while its own
    // `currentUser` is still loading — the normal state of a cold or
    // just-restarted worker, i.e. of any link opened in a fresh tab. That
    // `null` used to win the race below, so `connection_init` went out
    // anonymous and the first `Me` resolved to null: the user saw the page
    // logged out until they reloaded. Treat the worker as a fast path only —
    // ignore anything that is not a real token (and ignore its rejection when
    // there is no message channel), leaving Firebase as the authority on
    // whether there is a user at all.
    const getTokenFromSW = () =>
      new Promise<AuthTokenResult>((resolve) => {
        (getFirebaseTokenFromSW() as Promise<string | null>)
          .then((token) => {
            if (token) resolve({ token, source: 'serviceWorker' });
          })
          .catch(() => undefined);
      });

    this.connectionParamsFactory = async () => {
      const startedAt = Date.now();
      const tokenRace = Promise.race([
        getTokenFromSW(),
        getTokenFromFirebase(),
      ]);
      const { token, source } = await settleWithin(
        tokenRace,
        AUTH_TOKEN_TIMEOUT_MS,
        { token: null, source: 'timeout' } as AuthTokenResult,
      );
      reportHandshakeAuth(source, Date.now() - startedAt, !!token, tokenRace);
      return token ? { auth: { token } } : {};
    };

    this.openConnection();

    // Re-inject token whenever Firebase auth or token changes.
    firebaseAppAuth.onAuthStateChanged(() => this.sendToken());
    firebaseAppAuth.onIdTokenChanged(() => this.sendToken());

    // Heartbeat every 30 s.
    this.heartbeatInterval = setInterval(() => this.sendHeartbeat(), 30_000);

    // ── Custom Palco messages via WsConnector event bus ────────────────────
    WsConnector.on('message', (msg: { type: string; payload?: unknown }) =>
      this.rawSend(msg),
    );
    WsConnector.on('track_presence', (roomName: string) =>
      this.rawSend({ type: 'track_presence', payload: { roomName } }),
    );
    WsConnector.on('end_track_presence', (roomName: string) =>
      this.rawSend({ type: 'end_track_presence', payload: { roomName } }),
    );
  }

  // ── ApolloLink interface ──────────────────────────────────────────────────

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>((observer) => {
      const opId = this.nextOpId();
      const sub: ActiveSub = { observer, operation };

      if (this.authReady && this.ws?.readyState === WebSocket.OPEN) {
        this.subscriptions.set(opId, sub);
        this.sendStart(opId, operation);
      } else {
        this.pendingQueue.push({ opId, sub });
      }

      // Cleanup: unsubscribe when Apollo tears down the operation.
      return () => this.stopOperation(opId);
    });
  }

  // ── WebSocket lifecycle ───────────────────────────────────────────────────

  private openConnection(): void {
    if (this.connecting || this.connected) return;
    this.connecting = true;

    try {
      this.ws = new WebSocket(this.url, SUBPROTOCOL);

      this.ws.onopen = async () => {
        console.info('WS on connecting');
        const params = await this.connectionParamsFactory();
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({ type: GQL.CONNECTION_INIT, payload: params }),
          );
        }
      };

      this.ws.onmessage = ({ data }: MessageEvent<string>) => {
        let msg: { type: string; id?: string; payload?: unknown };
        try {
          msg = JSON.parse(data);
        } catch {
          return;
        }
        this.handleServerMessage(msg);
      };

      this.ws.onerror = (event) => {
        console.warn('WS error', event);
      };

      this.ws.onclose = () => {
        console.info('WS on disconnected');
        this.connecting = false;
        this.connected = false;
        this.authReady = false;
        this.last_sent_token = undefined;
        this.handleDisconnect();
      };
    } catch {
      this.connecting = false;
      this.scheduleReconnect();
    }
  }

  private handleServerMessage(msg: {
    type: string;
    id?: string;
    payload?: unknown;
  }): void {
    const { type, id, payload } = msg;

    switch (type) {
      case GQL.CONNECTION_ACK: {
        console.info('WS on connected');
        this.connecting = false;
        this.connected = true;
        this.reconnectAttempts = 0;
        WsConnector.emit(WS_CONNECTION_STATUS_EVENT, {
          state: 'connected',
        } as WsConnectionStatus);
        // Handled frontend conditions go to Grafana on this socket, limited to
        // what this backend announced it counts (services/observability).
        attachClientEventSender(
          (event) => this.rawSend({ type: 'client_event', payload: { event } }),
          (payload as { clientEvents?: unknown } | undefined)?.clientEvents,
        );
        // Deliver the auth token BEFORE flushing the queue. `sendToken` awaits
        // `getIdToken()`, so firing it without waiting let the queued
        // operations reach the server in the same tick, while the connection
        // was still the anonymous one of `connection_init` — which is how the
        // very first `Me` came back null.
        settleWithin(this.sendToken(), AUTH_TOKEN_TIMEOUT_MS, undefined).then(
          () => this.flushPendingQueue(),
        );
        break;
      }

      // Server keepalive ping — must respond with pong.
      case GQL.PING:
        this.ws?.send(JSON.stringify({ type: GQL.PONG }));
        break;

      case GQL.NEXT: {
        if (!id) break;
        const sub = this.subscriptions.get(id);
        if (!sub) break;
        const p = payload as { data?: unknown; errors?: unknown[] } | null;
        // GraphQL errors in the `next` payload are field-level errors, not
        // network errors. Pass them via observer.next so Apollo treats them
        // as graphQLErrors rather than networkError (which would require an
        // Error object and causes "Error message not found").
        sub.observer.next(p as FetchResult);
        break;
      }

      case GQL.ERROR: {
        if (!id) break;
        const sub = this.subscriptions.get(id);
        if (sub) {
          // `error` message payload is a GraphQL errors array from the server.
          // Wrap it in an Error so Apollo's networkError handler gets a proper object.
          const errors = Array.isArray(payload) ? payload : [payload];
          const err = new Error(
            (errors[0] as any)?.message ?? 'Subscription error',
          );
          (err as any).graphQLErrors = errors;
          sub.observer.error(err);
          this.subscriptions.delete(id);
        }
        break;
      }

      case GQL.SERVER_COMPLETE: {
        if (!id) break;
        const sub = this.subscriptions.get(id);
        if (sub) {
          sub.observer.complete();
          this.subscriptions.delete(id);
        }
        break;
      }
    }
  }

  private handleDisconnect(): void {
    detachClientEventSender();
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      // Re-queue active subscriptions so they restart after reconnection.
      const requeue = Array.from(this.subscriptions.entries()).map(
        ([opId, sub]) => ({ opId, sub }),
      );
      this.subscriptions.clear();
      this.pendingQueue.push(...requeue);
      this.scheduleReconnect();
    } else {
      // Give up — propagate error to all waiting observers.
      WsConnector.emit(WS_CONNECTION_STATUS_EVENT, {
        state: 'lost',
      } as WsConnectionStatus);
      this.subscriptions.forEach((sub) =>
        sub.observer.error(new Error('WebSocket connection lost')),
      );
      this.subscriptions.clear();
      this.pendingQueue.length = 0;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = Math.min(1_000 * 2 ** this.reconnectAttempts, 30_000);
    this.reconnectAttempts += 1;
    console.info(
      `WS reconnecting in ${delay} ms (attempt ${this.reconnectAttempts})`,
    );
    WsConnector.emit(WS_CONNECTION_STATUS_EVENT, {
      state: 'reconnecting',
      attempt: this.reconnectAttempts,
    } as WsConnectionStatus);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openConnection();
    }, delay);
  }

  // ── Protocol helpers ──────────────────────────────────────────────────────

  private sendStart(opId: string, operation: Operation): void {
    this.ws?.send(
      JSON.stringify({
        type: GQL.SUBSCRIBE,
        id: opId,
        payload: {
          query: print(operation.query),
          variables: operation.variables ?? {},
          operationName: operation.operationName ?? null,
          extensions: operation.extensions ?? {},
        },
      }),
    );
  }

  private stopOperation(opId: string): void {
    this.subscriptions.delete(opId);
    const idx = this.pendingQueue.findIndex((e) => e.opId === opId);
    if (idx !== -1) this.pendingQueue.splice(idx, 1);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: GQL.CLIENT_COMPLETE, id: opId }));
    }
  }

  // ── Custom Palco messages ─────────────────────────────────────────────────

  private rawSend(message: { type: string; payload?: unknown }): void {
    try {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
      }
    } catch (error) {
      console.warn(error);
    }
  }

  /**
   * Moves queued operations onto the wire. Runs a tick or more after
   * `connection_ack` (it waits for the token), so the socket may already have
   * closed — in which case `handleDisconnect` has re-queued everything and
   * this must not move it back out.
   */
  private flushPendingQueue(): void {
    if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) return;
    this.authReady = true;
    const queued = this.pendingQueue.splice(0);
    for (const { opId, sub } of queued) {
      this.subscriptions.set(opId, sub);
      this.sendStart(opId, sub.operation);
    }
  }

  private sendHeartbeat(): void {
    this.rawSend({ type: 'heartbeat' });
  }

  private async sendToken(): Promise<void> {
    try {
      const token = await this.firebaseAppAuth.currentUser?.getIdToken();
      if (token === this.last_sent_token) return;
      this.rawSend({ type: 'update_auth_token', payload: { auth: { token } } });
      this.last_sent_token = token;
    } catch (error) {
      console.warn('WS sendToken error', error);
    }
  }

  private nextOpId(): string {
    return String(++this.opIdCounter);
  }
}
